import React, { useState, useRef } from "react";
import Popover from "@material-ui/core/Popover";
import Tooltip from "@material-ui/core/Tooltip";

// icons
import IconButton from "@material-ui/core/IconButton";
import { CheckRounded as Check, FileCopyOutlined } from "@material-ui/icons";
import { color, spacingDefaults } from "style/constants";
import StyledGrid from "../StyledGrid";

interface TooltipsProps {
  text: string;
  handleClick(event: React.MouseEvent<HTMLButtonElement>): void;
  children: JSX.Element;
}

const SimpleTooltips: React.FC<TooltipsProps> = ({
  text,
  handleClick,
  children
}: TooltipsProps) => {
  return (
    <div>
      <Tooltip title={text}>
        <IconButton onClick={handleClick} aria-label={text.toLowerCase()}>
          {children}
        </IconButton>
      </Tooltip>
    </div>
  );
};

interface PopoverProps {
  title: string;
  content: string;
  children: JSX.Element;
  canCopy?: boolean;
}

const copyToClipboard = (textRef: React.MutableRefObject<null>): boolean => {
  const copyText = textRef.current;
  const selector = document.getSelection();
  if (!(copyText && selector)) return false;
  const range = new Range();
  range.selectNodeContents(copyText);
  selector.removeAllRanges();
  selector.addRange(range);
  document.execCommand("copy");
  selector.removeAllRanges();
  return true;
};

const CustomPopover: React.FC<PopoverProps> = ({
  title,
  content,
  children,
  canCopy
}: PopoverProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [copied, setCopied] = useState(false);
  const copyRef = useRef(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setCopied(false);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <SimpleTooltips text={title} handleClick={handleClick}>
        {children}
      </SimpleTooltips>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
      >
        <StyledGrid container padding={spacingDefaults.normal}>
          <StyledGrid item>
            <div ref={copyRef}>{content}</div>
          </StyledGrid>
          <StyledGrid item marginLeft={spacingDefaults.xsmall}>
            {canCopy &&
              (copied ? (
                <Check fontSize="small" stroke={color.GREEN} />
              ) : (
                <FileCopyOutlined
                  color="action"
                  fontSize="small"
                  onClick={() => setCopied(copyToClipboard(copyRef))}
                />
              ))}
          </StyledGrid>
        </StyledGrid>
      </Popover>
    </div>
  );
};

export default CustomPopover;
